import { useRef } from 'react';
import PriceIndicator from '../../components/PriceIndicator';

/**
 * DEV-ONLY preview of the real PriceIndicator in every approved v2 state
 * (REVERB_PRICING_AUDIT_2026-10-08.md Part 13). Reachable only on localhost
 * builds — App.js mounts the route exclusively when NODE_ENV === 'development'
 * — and exists so review screenshots come from the ACTUAL component, not
 * recreated markup. Docs below are the real dry-run numbers
 * (audit-evidence/dryrun-v1-v2.json); no Firestore involved.
 */

const NOW = Date.now();

function v2Doc({ tier, evidence, tierKey = 'new', verdictsEnabled = true, generatedAtMs = NOW }) {
  return {
    pricingMeta: { pipelineVersion: 2, verdictsEnabled, generatedAtMs },
    conditionsV2: { [tierKey]: tier },
    evidenceV2: { [tierKey]: evidence || [] },
    conditions: {},
  };
}

const entry = (id, cents, shop) => ({
  id, src: 'reverb', cents, shopSlug: shop, lastSeenAt: NOW, conditionSlug: 'new',
});

// TTO New: 18 US dealers, all $250 — single reference, verdict-grade.
const ttoNew = v2Doc({
  tier: {
    display: true, verdictEligible: true, low: 25000, mid: 25000, high: 25000,
    valueBasis: 'iqr', singlePrice: 25000, uniqueListings: 18, reverbListings: 18,
    pxcListings: 0, distinctSellers: 18, coverage: 'full', conditionCounts: { new: 18 },
  },
  evidence: Array.from({ length: 18 }, (_, i) => entry(`r${i}`, 25000, `shop${i}`)),
});

// DS-1 Good: full coverage, 18 sellers — the verdict state.
const ds1Good = v2Doc({
  tierKey: 'good',
  tier: {
    display: true, verdictEligible: true, low: 4500, mid: 5000, high: 6900,
    valueBasis: 'iqr', singlePrice: null, uniqueListings: 18, reverbListings: 18,
    pxcListings: 0, distinctSellers: 18, coverage: 'full',
    conditionCounts: { good: 18 },
  },
  evidence: [],
});

// DS-1 Excellent: 40 of 122, 29 sellers, PARTIAL coverage — range, no verdict
// (the representative-partial clause shipped disabled after its validation
// failed; see reverbPricingV2.js REPRESENTATIVE_PARTIAL_ENABLED).
const ds1Excellent = v2Doc({
  tierKey: 'excellent',
  tier: {
    display: true, verdictEligible: false, reasons: ['partial_not_representative'],
    low: 4200, mid: 4900, high: 5000,
    valueBasis: 'iqr', singlePrice: null, uniqueListings: 40, reverbListings: 40,
    pxcListings: 0, distinctSellers: 29, coverage: 'partial', apiTotal: 122,
    conditionCounts: { excellent: 40 },
  },
  evidence: [],
});

// TTO Excellent: 2 Excellent + 1 Mint fallback — observed span, limited data.
const ttoExcellent = v2Doc({
  tierKey: 'excellent',
  tier: {
    display: true, verdictEligible: false, reasons: ['mixed_conditions'],
    low: 15900, mid: 16300, high: 23000, valueBasis: 'span', singlePrice: null,
    uniqueListings: 3, reverbListings: 3, pxcListings: 0, distinctSellers: 3,
    coverage: 'full', mixedConditions: true,
    conditionCounts: { excellent: 2, mint: 1 },
  },
  evidence: [
    { ...entry('e1', 15900, 'bigbattlebear'), conditionSlug: 'excellent' },
    { ...entry('e2', 16300, 'ihaveabeard'), conditionSlug: 'excellent' },
    { ...entry('m1', 23000, 'geartree'), conditionSlug: 'mint', fallback: true },
  ],
});

// Legacy v1 doc (Behringer Centaur Overdrive Excellent).
const legacyDoc = {
  conditions: { excellent: { low: 16500, mid: 24900, high: 29700, sampleSize: 9 } },
};

const CASES = [
  { label: 'TTO New, no price entered (single reference)', doc: ttoNew,
    condition: 'New', price: '', decade: '', id: 'tto-new-plain' },
  { label: 'TTO New, $300 entered (single reference, warning fires)', doc: ttoNew,
    condition: 'New', price: 300, decade: '', id: 'tto-new' },
  { label: 'DS-1 Good, $48 entered (verdict, full coverage)', doc: ds1Good,
    condition: 'Good', price: 48, decade: '', id: 'ds1-good' },
  { label: 'DS-1 Excellent (range, partial coverage, no verdict)', doc: ds1Excellent,
    condition: 'Excellent', price: '', decade: '', id: 'ds1-exc' },
  { label: 'TTO Excellent (Excellent + Mint fallback, limited data)', doc: ttoExcellent,
    condition: 'Excellent', price: '', decade: '', id: 'tto-exc' },
  { label: 'Legacy v1 doc (prior data)', doc: legacyDoc,
    condition: 'Excellent', price: '', decade: '', id: 'legacy' },
];

export default function PricingPreview() {
  const anchorRef = useRef(null);
  return (
    <div style={{ maxWidth: 480, margin: '0 auto', padding: '2rem 16px', fontFamily: 'Nunito, sans-serif' }}>
      <h2 style={{ fontSize: '1.2rem' }}>Pricing preview (dev only)</h2>
      {CASES.map((c) => (
        <div key={c.id} id={`case-${c.id}`} style={{ marginBottom: '2.2rem' }}>
          <div style={{ fontSize: 12, color: '#6B7280', fontWeight: 700, marginBottom: 8 }}>
            {c.label}
          </div>
          <PriceIndicator
            make="Preview"
            model="Preview"
            condition={c.condition}
            price={c.price}
            decade={c.decade}
            priceInputRef={c.id === 'tto-new' ? anchorRef : undefined}
            pricingDocOverride={c.doc}
          />
          {c.id === 'tto-new' && <div ref={anchorRef} style={{ height: 1 }} />}
          <div style={{ marginTop: 8 }}>
            <PriceIndicator
              make="Preview"
              model="Preview"
              condition={c.condition}
              price={c.price}
              decade={c.decade}
              compact
              pricingDocOverride={c.doc}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
